// Helper function to get the number of days in a month
const getDaysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

// Helper function to get month names
const getMonthNames = () => {
  return [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
};

// Main function to fill gaps in data
export const fillGapsInData = (apiResponse, params) => {
  // If apiResponse is not in the expected format, return it as is
  if (!apiResponse || !apiResponse.data) {
    return apiResponse;
  }

  const { data_type, year, month, day } = params;
  const responseData = { ...apiResponse };
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11
  const currentDay = now.getDate();
  const currentHour = now.getHours();

  if (data_type === 'year' && responseData.data.year) {
    // Fill missing months for yearly data (only up to current month if current year)
    const monthNames = getMonthNames();
    const existingData = responseData.data.year;
    const filledYearData = {};
    const maxMonth = (year === currentYear) ? currentMonth : 12;
    
    // Determine the format used by the API (check if existing keys are lowercase)
    const existingKeys = Object.keys(existingData);
    const isLowercase = existingKeys.some(key => key === key.toLowerCase() && key !== key.toUpperCase());
    
    // Fill months in chronological order
    for (let i = 0; i < maxMonth; i++) {
      const monthName = isLowercase ? monthNames[i].toLowerCase() : monthNames[i];
      // Use existing data if available, otherwise set to 0
      filledYearData[monthName] = existingData[monthName] || existingData[monthNames[i]] || existingData[monthNames[i].toLowerCase()] || 0;
    }
    
    responseData.data.year = filledYearData;
  }
  else if (data_type === 'month' && responseData.data.month) {
    // Fill missing days for monthly data (only up to current day if current month/year)
    const daysInMonth = getDaysInMonth(year, month);
    const filledMonthData = { ...responseData.data.month }; // Preserve existing data
    let maxDay = daysInMonth;
    
    // If this is the current month and year, only fill up to current day
    if (year === currentYear && month === currentMonth) {
      maxDay = currentDay;
    }
    
    for (let day = 1; day <= maxDay; day++) {
      const dayKey = day.toString();
      // Only add if the day doesn't already exist
      if (!(dayKey in filledMonthData)) {
        filledMonthData[dayKey] = 0;
      }
    }
    
    responseData.data.month = filledMonthData;
  }
  else if (data_type === 'day' && responseData.data.day) {
    // Fill missing hours for daily data (only up to current hour if current day)
    const filledDayData = { ...responseData.data.day }; // Preserve existing data
    let maxHour = 23;
    
    // If this is the current day, month, and year, only fill up to current hour
    if (year === currentYear && month === currentMonth && day === currentDay) {
      maxHour = currentHour;
    }
    
    for (let hour = 0; hour <= maxHour; hour++) {
      const hourKey = hour.toString();
      // Only add if the hour doesn't already exist
      if (!(hourKey in filledDayData)) {
        filledDayData[hourKey] = 0;
      }
    }
    
    responseData.data.day = filledDayData;
  }
  else if (data_type === 'week') {
    // Fill gaps for the last 7 days
    const filledWeekData = {};
    const today = new Date();
    
    // Generate the last 7 days (including today) in chronological order
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayKey = date.getDate().toString();
      
      // Create a sequential key to maintain chronological order
      const sequentialKey = `day_${6 - i + 1}`;
      
      // Look for data in the combined monthly data
      let dayValue = 0;
      if (responseData.data && responseData.data.combinedMonth) {
        const rawValue = responseData.data.combinedMonth[dayKey];
        // Handle both number and { total: number } formats
        dayValue = typeof rawValue === 'object' && rawValue !== null
          ? (rawValue.total || 0)
          : (rawValue || 0);
      }
      
      // Store with both the day number for display and sequential ordering
      filledWeekData[sequentialKey] = {
        day: dayKey,
        value: dayValue,
        date: date.toISOString().split('T')[0] // YYYY-MM-DD format
      };
    }
    
    responseData.data.week = filledWeekData;
  }

  return responseData;
};

// Transform new analytics history format to chart-compatible format
// Input format: { data: { total, "1": { total, "1": { total } } }, status: 200 }
// Output format: [{ key: string, value: number }, ...]
export const transformUsageHistory = (historyResponse, dataType, year, month) => {
  if (!historyResponse || !historyResponse.data) {
    return [];
  }

  const data = historyResponse.data;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();
  const monthNames = getMonthNames();

  if (dataType === 'year') {
    // Extract monthly totals for yearly view
    const chartData = [];
    const targetYear = year || currentYear;
    const maxMonth = (targetYear === currentYear) ? currentMonth : 12;

    for (let m = 1; m <= maxMonth; m++) {
      const monthKey = m.toString();
      const monthData = data[monthKey];
      chartData.push({
        key: monthNames[m - 1],
        value: monthData?.total || 0
      });
    }
    return chartData;
  }

  if (dataType === 'month') {
    // Extract daily totals for monthly view
    const chartData = [];
    const targetMonth = month || currentMonth;
    const targetYear = year || currentYear;
    const monthData = data[targetMonth.toString()];

    if (!monthData) {
      return [];
    }

    const daysInMonth = getDaysInMonth(targetYear, targetMonth);
    const maxDay = (targetYear === currentYear && targetMonth === currentMonth) ? currentDay : daysInMonth;

    for (let d = 1; d <= maxDay; d++) {
      const dayKey = d.toString();
      const dayData = monthData[dayKey];
      chartData.push({
        key: dayKey,
        value: dayData?.total || 0
      });
    }
    return chartData;
  }

  if (dataType === 'week') {
    // Extract last 7 days
    const chartData = [];
    const today = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayNum = date.getDate();
      const monthNum = date.getMonth() + 1;

      const monthData = data[monthNum.toString()];
      const dayData = monthData?.[dayNum.toString()];

      chartData.push({
        key: dayNum.toString(),
        value: dayData?.total || 0,
        date: date.toISOString().split('T')[0]
      });
    }
    return chartData;
  }

  if (dataType === 'total') {
    // Return total as single item
    return [{ key: 'total', value: data.total || 0 }];
  }

  return [];
};

// Get total usage from new history format
export const getTotalFromHistory = (historyResponse) => {
  if (!historyResponse || !historyResponse.data) {
    return 0;
  }
  return historyResponse.data.total || 0;
};

// Get period usage (month or year) from new history format
export const getPeriodUsageFromHistory = (historyResponse, period, year, month) => {
  if (!historyResponse || !historyResponse.data) {
    return 0;
  }

  const data = historyResponse.data;
  const now = new Date();
  const targetYear = year || now.getFullYear();
  const targetMonth = month || (now.getMonth() + 1);

  if (period === 'month') {
    const monthData = data[targetMonth.toString()];
    return monthData?.total || 0;
  }

  if (period === 'year') {
    // Sum all months in the data (assumes data is for the target year)
    let yearTotal = 0;
    for (let m = 1; m <= 12; m++) {
      const monthData = data[m.toString()];
      if (monthData?.total) {
        yearTotal += monthData.total;
      }
    }
    return yearTotal;
  }

  return data.total || 0;
};
